import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useSignupFlow } from '../hooks/useSignupFlow';
import { createClinic, createUser, fetchSpecializations, fetchPlans, tokenizeCard, subscribe } from '../services/signupApi';
import { SignupLayout } from '../components/signup/SignupLayout';
import { SignupLeftLayout } from '../components/signup/SignupLeftLayout';
import { SignupRightLayout } from '../components/signup/SignupRightLayout';
import { AnimatedCard3D } from '../components/signup/AnimatedCard3D';
import { ClinicCreation } from '../components/signup/ClinicCreation';
import { UserCreation } from '../components/signup/UserCreation';
import { PaymentPreview } from '../components/signup/PaymentPreview';
import { PaymentForm } from '../components/signup/PaymentForm';
import type { ClinicFormData, UserFormData, Specialization, SubscriptionPlan, CardFormData } from '../types/signup';
import '../styles/signup.css';

export const Signup = () => {
  const navigate = useNavigate();
  const {
    step,
    clinicData,
    clinicCreated,
    loading,
    error,
    setClinicData,
    setClinicCreated,
    setLoading,
    setError,
    nextStep,
    prevStep,
  } = useSignupFlow();

  // Specializations state
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [specializationsLoading, setSpecializationsLoading] = useState(true);

  // Real-time clinic data for live preview
  const [currentClinicName, setCurrentClinicName] = useState(clinicData?.name || '');
  const [currentSpecializationId, setCurrentSpecializationId] = useState<number | undefined>(
    clinicData?.specialization_id
  );

  // Billing / payment state
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  // Real-time card preview data
  const [previewCardData, setPreviewCardData] = useState<CardFormData>({
    card_holder: '', card_number: '', expiry_month: '', expiry_year: '', cvv: '', customer_phone: '',
  });
  const [previewPlanId, setPreviewPlanId] = useState<number | null>(null);
  // 3DS auth url (set when server returns authentication_required)
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  // Pending subscribe payload for 3DS retry
  const [pendingSubscribe, setPendingSubscribe] = useState<Parameters<typeof subscribe>[0] | null>(null);

  // Fetch specializations on mount
  useEffect(() => {
    const loadSpecializations = async () => {
      try {
        const data = await fetchSpecializations();
        setSpecializations(data);
        // Set default specialization if not already set
        if (!currentSpecializationId && data.length > 0) {
          setCurrentSpecializationId(data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch specializations:', err);
        setError('Failed to load specializations. Please refresh the page.');
      } finally {
        setSpecializationsLoading(false);
      }
    };

    loadSpecializations();
  }, []);

  // Step 1: Create Clinic
  const handleClinicSubmit = async (data: ClinicFormData) => {
    setError(null);
    setLoading(true);

    try {
      const createdClinic = await createClinic(data);
      setClinicData(data);
      setClinicCreated(createdClinic);
      nextStep();
    } catch (err: any) {
      setError(err.message || 'Failed to create clinic. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Create Firebase User + Backend User
  const handleUserSubmit = async (userData: UserFormData) => {
    if (!clinicCreated) {
      setError('Clinic information is missing. Please start over.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // 1. Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );
      const firebase_uid = userCredential.user.uid;

      // Set the Firebase display name so the profile avatar and greeting
      // can show the real name going forward.
      await updateProfile(userCredential.user, {
        displayName: `${userData.first_name} ${userData.last_name}`.trim(),
      });

      // 2. Create backend user
      await createUser({
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        firebase_uid,
        clinic_id: clinicCreated.id,
      });

      // 3. Get auth token and store it
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);
      localStorage.setItem('userEmail', userData.email);

      // 4. Advance to payment step (do NOT navigate yet)
      nextStep();
    } catch (err: any) {
      // Handle errors
      let errorMessage = 'Failed to create account. Please try again.';

      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered with Firebase.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load plans when entering step 3
  useEffect(() => {
    if (step !== 3) return;
    setPlansLoading(true);
    fetchPlans()
      .then((data) => {
        setPlans(data);
        if (data.length > 0) setPreviewPlanId(data[0].planId);
      })
      .catch(() => setError('No se pudieron cargar los planes. Por favor recarga la página.'))
      .finally(() => setPlansLoading(false));
  }, [step]);

  // Step 3: Tokenize + Subscribe
  const handlePaymentSubmit = async (cardData: CardFormData, planId: number) => {
    if (!clinicCreated) {
      setError('Falta información de la clínica. Por favor comienza de nuevo.');
      return;
    }

    setError(null);
    setLoading(true);
    setAuthUrl(null);
    let requiresAuth = false;

    try {
      // Step 3a — tokenize the card
      const tokenResult = await tokenizeCard({
        customer_id: `clinic_${clinicCreated.id}`,
        customer_name: cardData.card_holder,
        customer_email: localStorage.getItem('userEmail') || '',
        customer_phone: cardData.customer_phone,
        card_number: cardData.card_number.replace(/\s/g, ''),
        card_holder: cardData.card_holder,
        expiry_month: cardData.expiry_month,
        expiry_year: cardData.expiry_year,
        cvv: cardData.cvv,
      });

      // Step 3b — subscribe
      const isUsaCan = ['USA', 'CAN'].includes(tokenResult.card_country_code?.toUpperCase() || '');
      const subscribePayload = {
        plan_id: planId,
        payment_method_id: tokenResult.payment_method_id,
        backup_payment_method_id: '',
        card_brand: tokenResult.card_brand,
        card_last_four: tokenResult.card_last_four,
        card_bin: tokenResult.card_bin,
        billing_country_code: isUsaCan ? tokenResult.card_country_code : '',
        billing_state_code: '',
        billing_zip_code: '',
        authentication_id: null,
      };

      setPendingSubscribe(subscribePayload);
      const subResult = await subscribe(subscribePayload);

      if (subResult.status === 'succeeded') {
        navigate('/home');
      } else if (subResult.status === 'authentication_required') {
        requiresAuth = true;
        setAuthUrl(subResult.auth_url);
        // loading stays true until 3DS completes
      } else {
        throw new Error(subResult.error || 'El pago fue rechazado. Por favor intenta con otra tarjeta.');
      }
    } catch (err: any) {
      setError(err.message || 'Error al procesar el pago. Por favor intenta de nuevo.');
    } finally {
      if (!requiresAuth) setLoading(false);
    }
  };

  // 3DS retry: called with authenticationId received from iframe postMessage
  const handleAuthComplete = async (authenticationId: string) => {
    if (!pendingSubscribe) return;
    setError(null);
    try {
      const retryPayload = { ...pendingSubscribe, authentication_id: authenticationId };
      const subResult = await subscribe(retryPayload);

      if (subResult.status === 'succeeded') {
        navigate('/home');
      } else if (subResult.status === 'authentication_required') {
        // Another round of 3DS (unlikely)
        setAuthUrl(subResult.auth_url);
      } else {
        setAuthUrl(null);
        throw new Error(subResult.error || 'Autenticación fallida. Intenta con otra tarjeta.');
      }
    } catch (err: any) {
      setAuthUrl(null);
      setError(err.message || 'Error al completar la autenticación.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#fcfdfe] font-display text-[#101822] min-h-screen overflow-auto w-screen flex selection:bg-primary selection:text-white">
      <SignupLayout>
        <SignupLeftLayout>
          {step === 3 ? (
            <PaymentPreview
              cardHolder={previewCardData.card_holder}
              cardNumber={previewCardData.card_number}
              expiryMonth={previewCardData.expiry_month}
              expiryYear={previewCardData.expiry_year}
              selectedPlan={plans.find((p) => p.planId === previewPlanId) ?? null}
            />
          ) : (
            <AnimatedCard3D
              clinicName={currentClinicName}
              specializationId={currentSpecializationId}
              specializations={specializations}
            />
          )}
        </SignupLeftLayout>
        <SignupRightLayout>
          {step === 1 && (
            <ClinicCreation
              onSubmit={handleClinicSubmit}
              loading={loading || specializationsLoading}
              error={error}
              initialData={clinicData}
              onNameChange={setCurrentClinicName}
              onSpecializationChange={setCurrentSpecializationId}
              specializations={specializations}
            />
          )}
          {step === 2 && (
            <UserCreation
              onSubmit={handleUserSubmit}
              onBack={prevStep}
              loading={loading}
              error={error}
            />
          )}
          {step === 3 && (
            <PaymentForm
              plans={plans}
              plansLoading={plansLoading}
              onSubmit={handlePaymentSubmit}
              onBack={prevStep}
              loading={loading}
              error={error}
              authUrl={authUrl}
              onAuthComplete={handleAuthComplete}
              onAuthError={() => {
                setAuthUrl(null);
                setLoading(false);
                setError('La autenticación 3DS falló o expiró. Por favor intenta con otra tarjeta.');
              }}
              onCardDataChange={(data, planId) => {
                setPreviewCardData(data);
                setPreviewPlanId(planId);
              }}
            />
          )}
        </SignupRightLayout>
      </SignupLayout>
    </div>
  );
};
